/**
 * Emails sent (MB, 2 Nov 2026; docs/specs/cloud/app-email.md §4 and §12; help `cloud.email-log`): the Company page's
 * tab for tenant admins listing every email Teslyn has sent for the organisation (the client report emails, Email…
 * messages) with who it went to, its subject, when and whether it went. The outbox's message bodies are never read:
 * the page asks for the columns it shows. Who may read is the database's rule (tenant admins, `email_outbox_select`).
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { SelectField } from '../components/Fields.tsx';
import { ErrorState, Loading, SectionHeader } from '../components/States.tsx';
import { loadEmailLog, type EmailLogRow } from './api.ts';
import { TableWrap } from '../components/TableWrap.tsx';

type Shown = 'all' | 'sent' | 'failed';

const WORDS =
  'Every email Teslyn has sent for your organisation: the client report emails and messages sent with Email…. Sign-in emails are not listed.';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "28 Oct 2026 18:01" in the viewer's own time zone. */
export function whenText(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** The kind of email in words. */
export function kindWord(kind: EmailLogRow['kind']): string {
  return kind === 'report_published' ? 'Report published' : 'Email…';
}

/** The email's state in words, a failed one with its attempts and the mail service's reason. */
export function statusWord(r: EmailLogRow): string {
  switch (r.status) {
    case 'sent':
      return 'Sent';
    case 'failed':
      return `Failed after ${r.attempts} ${r.attempts === 1 ? 'attempt' : 'attempts'}${r.error ? `: ${r.error}` : ''}`;
    case 'cancelled':
      return 'Cancelled';
    default:
      return 'Waiting to send';
  }
}

/** "3 emails: 1 sent, 1 failed, 1 waiting." */
export function summaryText(rows: readonly EmailLogRow[]): string {
  const n = (s: EmailLogRow['status']): number => rows.filter((r) => r.status === s).length;
  const parts = [
    `${n('sent')} sent`,
    `${n('failed')} failed`,
    ...(n('pending') > 0 ? [`${n('pending')} waiting`] : []),
    ...(n('cancelled') > 0 ? [`${n('cancelled')} cancelled`] : []),
  ];
  return `${rows.length} ${rows.length === 1 ? 'email' : 'emails'}: ${parts.join(', ')}.`;
}

export function EmailLogPanel({
  sb,
  tenantId,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
}): ReactElement {
  const [rows, setRows] = useState<EmailLogRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shown, setShown] = useState<Shown>('all');

  const load = useCallback(() => {
    setError(null);
    loadEmailLog(sb, tenantId)
      .then(setRows)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [sb, tenantId]);
  useEffect(load, [load]);

  const listed = useMemo(
    () =>
      (rows ?? []).filter((r) =>
        shown === 'all' ? true : shown === 'sent' ? r.status === 'sent' : r.status === 'failed',
      ),
    [rows, shown],
  );

  return (
    <section className="stack" aria-label="Emails sent">
      <SectionHeader title="Emails sent" subtitle={WORDS} />
      {error && <ErrorState message={error} onRetry={load} />}
      {!rows && !error && <Loading what="the emails sent" />}
      {rows && rows.length === 0 && (
        <div className="panel">
          <p className="body">Teslyn has sent no email for your organisation yet.</p>
        </div>
      )}
      {rows && rows.length > 0 && (
        <>
          <div className="row row--wrap">
            <SelectField<Shown>
              label="Show"
              value={shown}
              onChange={setShown}
              options={[
                ['all', 'All emails'],
                ['sent', 'Sent'],
                ['failed', 'Failed'],
              ]}
            />
            <p className="caption muted" role="status">
              {summaryText(rows)}
            </p>
          </div>
          {listed.length === 0 ? (
            <div className="panel">
              <p className="body">No {shown} emails.</p>
            </div>
          ) : (
            <TableWrap>
              <table className="table" aria-label="Emails sent">
                <thead>
                  <tr>
                    <th className="label">To</th>
                    <th className="label">Subject</th>
                    <th className="label">What</th>
                    <th className="label">When</th>
                    <th className="label">State</th>
                  </tr>
                </thead>
                <tbody>
                  {listed.map((r) => (
                    <tr key={r.id}>
                      <td>
                        {r.toName ? <span className="body">{r.toName}</span> : null}
                        {r.toName ? <br /> : null}
                        <span className="caption muted data">{r.toEmail}</span>
                      </td>
                      <td>{r.subject}</td>
                      <td>{kindWord(r.kind)}</td>
                      <td className="data">{whenText(r.sentAt ?? r.createdAt)}</td>
                      <td>{statusWord(r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          )}
        </>
      )}
    </section>
  );
}
